// Product detail (Unleashed "Stock Enquiry" parity — Stuart's 2026-08-26
// ruling, nav.ts; taken to full parity 2026-08-26 per Stuart's follow-up
// ruling: "product pages significantly less rich than Unleashed — I want
// access to all of these details and numbers"). Header + a headline numbers
// rail (Stock on hand/Allocated/Available/On purchase/Stock value/Avg
// cost/Last cost) always visible, then three panels — Inventory, Transactions,
// Costs — mirroring Unleashed's own Stock Enquiry tabs. Tabs precedent:
// features/sales-invoicing/CustomersAndPrices.tsx's Customers/Prices split;
// Mantine's Tabs keeps every panel mounted by default (`keepMounted: true`,
// Tabs.mjs), it just toggles `display: none` on the inactive ones — so this
// screen fires all three of its data queries up front rather than lazily on
// tab click (acceptable: same product, same page, no extra network cost the
// user wouldn't pay anyway by switching tabs).
//
// All data now comes from ONE call, GET /reporting/product-detail?productId=
// (services/api/src/modules/reporting/products.ts) — the master row, the
// headline totals, and the per-site/per-location breakdown together. This
// supersedes the old GET /config/products/:id + GET /reporting/stock-on-hand
// pairing (see types.ts's `ProductDetailDto` header for the full contract).
import { Alert, Badge, Button, Group, Loader, Paper, SimpleGrid, Stack, Table, Tabs, Text, ThemeIcon, Title, Tooltip } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { IconAlertTriangle, IconBoxOff, IconCoin, IconInfoCircle, IconListSearch, IconPackages } from '@tabler/icons-react';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../../../auth/permissions';
import { EmptyState } from '../../../components/EmptyState';
import { PRODUCT_TYPE_LABELS, VAT_TREATMENT_LABELS } from '../../../lib/enumLabels';
import { useProductDetail } from '../api';
import { formatCostPerUom, formatDate, formatMoneyCents, formatQty } from '../format';
import type { ProductDetailProductDto, ProductDetailSiteRow, ProductDetailTotals } from '../types';
import { ProductCostHistoryTable } from './ProductCostHistoryTable';
import { ProductTransactionsTable } from './ProductTransactionsTable';

/** One tile in the headline numbers rail. Every stat gets a plain-English
 * tooltip (guidance convention, 2026-08-25 pass — "every number explains
 * itself to a new user") since these are exactly the Unleashed terms
 * (Allocated/Available/On purchase) a Zebra Stock-only user has never seen. */
function StatCard({ label, value, sub, tooltip }: { label: string; value: string; sub?: string; tooltip: string }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Group gap={4} wrap="nowrap">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {label}
        </Text>
        <Tooltip multiline w={260} label={tooltip}>
          <ThemeIcon variant="subtle" size="sm" color="gray">
            <IconInfoCircle size={14} />
          </ThemeIcon>
        </Tooltip>
      </Group>
      <Text size="lg" fw={700}>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="dimmed">
          {sub}
        </Text>
      )}
    </Paper>
  );
}

/** The Inventory panel: product-wide min level/reorder qty, then a per-site
 * Paper (avg cost + value header, per-location table) exactly like the old
 * "Stock & cost basis" section, now with allocated/on-purchase/value columns
 * added, plus a Global "All sites" totals row (Unleashed's own layout: "an
 * Inventory per-warehouse table ... + Global totals row"). */
function InventorySection({
  product,
  totals,
  sites,
}: {
  product: ProductDetailProductDto;
  totals: ProductDetailTotals;
  sites: ProductDetailSiteRow[];
}) {
  return (
    <Stack gap="md">
      <Group gap="lg">
        <Text size="sm" c="dimmed">
          Min level (product-wide): <strong>{product.minLevel !== null ? formatQty(product.minLevel) : '—'}</strong>
        </Text>
        <Text size="sm" c="dimmed">
          Reorder qty (product-wide): <strong>{product.reorderQty !== null ? formatQty(product.reorderQty) : '—'}</strong>
        </Text>
      </Group>

      {sites.length === 0 ? (
        <EmptyState icon={IconBoxOff} title="No stock recorded for this product" hint="Nothing has been received, produced or counted yet." />
      ) : (
        <>
          {sites.map((site) => (
            <Paper key={site.siteId} withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs" wrap="wrap">
                <Text fw={600}>
                  {site.siteCode} — {site.siteName}
                </Text>
                <Group gap="lg">
                  <Text size="sm" c="dimmed">
                    Avg cost: <strong>{formatCostPerUom(site.avgCostCentsPerUnit, product.baseUom)}</strong>
                  </Text>
                  <Text size="sm" c="dimmed">
                    Value: <strong>{formatMoneyCents(site.valueCents)}</strong>
                  </Text>
                </Group>
              </Group>
              <Table verticalSpacing="xs">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Location</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Qty</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Allocated</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>On purchase</Table.Th>
                    <Table.Th style={{ textAlign: 'right' }}>Value</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {site.locations.map((loc) => (
                    <Table.Tr key={loc.locationId}>
                      <Table.Td>
                        {loc.code} — {loc.name}
                      </Table.Td>
                      <Table.Td>{loc.type}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatQty(loc.qty)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatQty(loc.allocatedQty)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatQty(loc.onPurchaseQty)}</Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>{formatMoneyCents(loc.valueCents)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          ))}

          <Paper withBorder radius="md" p="md" bg="var(--mantine-color-default-hover)">
            <Group justify="space-between" wrap="wrap">
              <Text fw={700}>All sites</Text>
              <Group gap="lg">
                <Text size="sm">
                  Qty: <strong>{formatQty(totals.qty)}</strong>
                </Text>
                <Text size="sm">
                  Allocated: <strong>{formatQty(totals.allocatedQty)}</strong>
                </Text>
                <Text size="sm">
                  On purchase: <strong>{formatQty(totals.onPurchaseQty)}</strong>
                </Text>
                <Text size="sm">
                  Value: <strong>{formatMoneyCents(totals.valueCents)}</strong>
                </Text>
              </Group>
            </Group>
          </Paper>

          <Text size="xs" c="dimmed">
            Per-location value is approximate — the site's average cost multiplied by the location's quantity. Cost is tracked per
            site, not per location.
          </Text>
        </>
      )}
    </Stack>
  );
}

export function ProductDetailScreen({ productId }: { productId: string }) {
  const isAdmin = useIsAdmin();
  const detailQuery = useProductDetail(productId);

  if (detailQuery.isLoading) {
    return (
      <Group>
        <Loader size="sm" />
        <Text c="dimmed">Loading product…</Text>
      </Group>
    );
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not load product">
        {detailQuery.error instanceof ApiRequestError ? detailQuery.error.message : 'Failed to load this product.'}
      </Alert>
    );
  }

  const { product, totals, sites } = detailQuery.data;

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <div>
          <Title order={2}>
            {product.sku} — {product.name}
          </Title>
          <Group gap="xs" mt={4}>
            <Badge variant="light">{PRODUCT_TYPE_LABELS[product.type]}</Badge>
            <Badge variant="light" color="gray">
              {product.baseUom}
            </Badge>
            <Badge color={product.isActive ? 'brandGreen' : 'gray'} variant="light">
              {product.isActive ? 'Active' : 'Inactive'}
            </Badge>
            {product.isMoistureTracked && (
              <Badge variant="light" color="blue">
                Moisture tracked{product.defaultMcPct ? ` (default ${product.defaultMcPct}%)` : ''}
              </Badge>
            )}
            <Badge variant="light" color="grape">
              {VAT_TREATMENT_LABELS[product.vatTreatment]}
            </Badge>
          </Group>
        </div>
        {isAdmin && (
          <Button component={Link} to="/settings/products" variant="default">
            Edit in Settings
          </Button>
        )}
      </Group>

      <div role="group" aria-label="Headline numbers">
        <SimpleGrid cols={{ base: 2, xs: 3, md: 4, lg: 7 }} spacing="sm">
          <StatCard
            label="Stock on hand"
            value={`${formatQty(totals.qty)} ${product.baseUom}`}
            tooltip="Total quantity of this product physically on hand, across every site and location."
          />
          <StatCard
            label="Allocated"
            value={`${formatQty(totals.allocatedQty)} ${product.baseUom}`}
            tooltip="Quantity already committed to open sales orders or dispatches — earmarked, but not yet shipped."
          />
          <StatCard
            label="Available"
            value={`${formatQty(totals.availableQty)} ${product.baseUom}`}
            tooltip="Stock on hand minus allocated — what's actually free to sell or use right now."
          />
          <StatCard
            label="On purchase"
            value={`${formatQty(totals.onPurchaseQty)} ${product.baseUom}`}
            tooltip="Quantity on open purchase orders that hasn't been received yet."
          />
          <StatCard
            label="Stock value"
            value={formatMoneyCents(totals.valueCents)}
            tooltip="Total value of stock on hand, valued at each site's weighted average cost."
          />
          <StatCard
            label="Avg cost"
            value={formatCostPerUom(totals.avgCostCentsPerUnit, product.baseUom)}
            tooltip="Weighted average cost across all sites — what outbound stock is relieved at right now."
          />
          <StatCard
            label="Last cost"
            value={formatCostPerUom(totals.lastCostCentsPerUnit, product.baseUom)}
            sub={totals.lastCostAt ? `as of ${formatDate(totals.lastCostAt)}` : undefined}
            tooltip="The most recent goods-receipt cost recorded for this product."
          />
        </SimpleGrid>
      </div>

      <Tabs defaultValue="inventory">
        <Tabs.List>
          <Tabs.Tab value="inventory" leftSection={<IconPackages size={16} />}>
            Inventory
          </Tabs.Tab>
          <Tabs.Tab value="transactions" leftSection={<IconListSearch size={16} />}>
            Transactions
          </Tabs.Tab>
          <Tabs.Tab value="costs" leftSection={<IconCoin size={16} />}>
            Costs
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="inventory" pt="md">
          <InventorySection product={product} totals={totals} sites={sites} />
        </Tabs.Panel>

        <Tabs.Panel value="transactions" pt="md">
          <ProductTransactionsTable productId={productId} baseUom={product.baseUom} />
        </Tabs.Panel>

        <Tabs.Panel value="costs" pt="md">
          <ProductCostHistoryTable productId={productId} totals={totals} baseUom={product.baseUom} />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
